import { DEFENDER, isDefenderPhase, nextPhase, type DefenderPhase } from '@sq/shared';

/**
 * Autosave for the run in progress on this device. Written when the attempt starts and after every round, so a
 * reload offers one-tap Resume at the start of the next round with the same attempt id and seed.
 */
export type SavedRun = {
  v: 3;
  /** Server attempt id; null when the run started offline (progression only). */
  attemptId: string | null;
  seed: number;
  accessible: boolean;
  /** Last finished round, or null while Round 1 is still in play. */
  completedPhase: DefenderPhase | null;
  /** Merged coarse round data from onPhase (round scores, placements, flags). */
  data: Record<string, number | string | boolean>;
  savedAt: number;
};

/** Matches the server's MAX_ATTEMPT_AGE_SECONDS: older runs can no longer be completed, so they aren't offered. */
const MAX_AGE_MS = 6 * 3600_000;
const key = (eventSlug: string) => `sq:run:${eventSlug}`;
const PLAYED_KEY = 'sq:played';

export function loadRun(eventSlug: string): SavedRun | null {
  try {
    const r = JSON.parse(localStorage.getItem(key(eventSlug)) ?? 'null') as SavedRun | null;
    if (!r || r.v !== 3 || typeof r.seed !== 'number') return null;
    if (r.completedPhase !== null && !isDefenderPhase(r.completedPhase)) return null;
    if (Date.now() - r.savedAt > MAX_AGE_MS) return null;
    return r;
  } catch {
    return null;
  }
}

export function saveRun(eventSlug: string, run: Omit<SavedRun, 'v' | 'savedAt'>): SavedRun {
  const r: SavedRun = { ...run, v: 3, savedAt: Date.now() };
  try {
    localStorage.setItem(key(eventSlug), JSON.stringify(r));
  } catch {
    /* storage unavailable: no resume after reload, play continues */
  }
  return r;
}

export function clearRun(eventSlug: string) {
  try {
    localStorage.removeItem(key(eventSlug));
  } catch {
    /* ignore */
  }
}

/** Human label for the Resume button. */
export function resumeLabel(run: SavedRun): string {
  const next = nextPhase(run.completedPhase);
  const def = DEFENDER.phases.find((p) => p.id === next);
  return def ? `Resume ${def.label}` : 'Finish your run';
}

/** Per-round results of the last finished run on this device, for the results screen (survives a reload). */
export type RunSummary = { rounds: { score: number; prompts: number; blocked: number; allowed: number; breaches: number }[]; total: number; assist: boolean };
const SUMMARY_KEY = (eventSlug: string) => `sq:summary:${eventSlug}`;

export function summarize(d: Record<string, number | string | boolean>): RunSummary {
  const n = (k: string) => (typeof d[k] === 'number' ? (d[k] as number) : 0);
  const rounds = ['round1', 'round2', 'round3'].map((k) => ({
    score: n(`${k}Score`), prompts: n(`${k}Prompts`), blocked: n(`${k}Blocked`), allowed: n(`${k}Allowed`), breaches: n(`${k}Breaches`),
  }));
  return { rounds, total: rounds.reduce((a, r) => a + r.score, 0), assist: d.assist === true };
}
export function saveSummary(eventSlug: string, s: RunSummary) {
  try {
    localStorage.setItem(SUMMARY_KEY(eventSlug), JSON.stringify(s));
  } catch {
    /* ignore */
  }
}
export function loadSummary(eventSlug: string): RunSummary | null {
  try {
    const s = JSON.parse(localStorage.getItem(SUMMARY_KEY(eventSlug)) ?? 'null') as RunSummary | null;
    return s && Array.isArray(s.rounds) && s.rounds.length === 3 ? s : null;
  } catch {
    return null;
  }
}

/** First-run flag: the game shows its short gesture demo only on a device that hasn't played before. */
export function hasPlayed(): boolean {
  try {
    return localStorage.getItem(PLAYED_KEY) === '1';
  } catch {
    return false;
  }
}
export function markPlayed() {
  try {
    localStorage.setItem(PLAYED_KEY, '1');
  } catch {
    /* ignore */
  }
}
